import {
  parseLearningContentWorksheetDocument,
  type LearningContentWorksheetDocumentV1,
} from '../types/learningContentWorksheetCanvas';

const STORAGE_PREFIX = 'cp:learning-worksheet-draft:v1:';
/** 예전 로컬 자동 저장(삭제됨)이 쓰던 '마지막 저장 시각' 키. 정리용으로만 남긴다 */
const SAVED_AT_PREFIX = 'cp:learning-worksheet-draft-saved-at:v1:';

export const LEARNING_CONTENT_WORKSHEET_DRAFTS_CHANGED_EVENT = 'cp:learning-worksheet-drafts-changed';

function notifyWorksheetDraftsChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent(LEARNING_CONTENT_WORKSHEET_DRAFTS_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

function draftStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** annualCellKey 또는 이미 prefix가 붙은 storage key 모두 수용 */
export function normalizeWorksheetDraftRowKey(rowKeyOrStorageKey: string): string {
  let key = rowKeyOrStorageKey.trim();
  while (key.startsWith(STORAGE_PREFIX)) {
    key = key.slice(STORAGE_PREFIX.length).trim();
  }
  return key;
}

export function buildLearningContentWorksheetDraftStorageKey(rowKey: string): string {
  const normalized = normalizeWorksheetDraftRowKey(rowKey);
  if (!normalized) return '';
  return `${STORAGE_PREFIX}${normalized}`;
}

function listDraftStorageKeysForRow(rowKeyOrStorageKey: string): string[] {
  const normalized = normalizeWorksheetDraftRowKey(rowKeyOrStorageKey);
  if (!normalized) return [];
  const canonical = buildLearningContentWorksheetDraftStorageKey(normalized);
  const legacyDoublePrefixed = `${STORAGE_PREFIX}${canonical}`;
  return legacyDoublePrefixed === canonical ? [canonical] : [canonical, legacyDoublePrefixed];
}

export function loadLearningContentWorksheetDraft(
  rowKeyOrStorageKey: string
): LearningContentWorksheetDocumentV1 | null {
  const storage = draftStorage();
  if (!storage) return null;
  for (const storageKey of listDraftStorageKeysForRow(rowKeyOrStorageKey)) {
    try {
      const raw = storage.getItem(storageKey);
      if (!raw) continue;
      const parsed = parseLearningContentWorksheetDocument(JSON.parse(raw));
      if (parsed) return parsed;
    } catch {
      continue;
    }
  }
  return null;
}

/** 예전 자동 저장이 남긴 시각 키를 모두 지운다 (초안 본문은 다른 화면이 읽으므로 그대로) */
export function purgeLearningWorksheetDraftSavedAtKeys(): void {
  const storage = draftStorage();
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(SAVED_AT_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/** @returns 저장에 성공하면 true (용량 초과 등은 false) */
export function saveLearningContentWorksheetDraft(
  rowKeyOrStorageKey: string,
  document: LearningContentWorksheetDocumentV1
): boolean {
  const normalized = normalizeWorksheetDraftRowKey(rowKeyOrStorageKey);
  if (!normalized) return false;
  const storage = draftStorage();
  if (!storage) return false;
  try {
    const canonical = buildLearningContentWorksheetDraftStorageKey(normalized);
    storage.setItem(canonical, JSON.stringify(document));
    for (const legacyKey of listDraftStorageKeysForRow(normalized)) {
      if (legacyKey !== canonical) storage.removeItem(legacyKey);
    }
    notifyWorksheetDraftsChanged();
    return true;
  } catch {
    /* localStorage quota */
    return false;
  }
}

export function clearLearningContentWorksheetDraft(rowKeyOrStorageKey: string): void {
  const storage = draftStorage();
  if (!storage) return;
  let removed = false;
  try {
    for (const storageKey of listDraftStorageKeysForRow(rowKeyOrStorageKey)) {
      if (!storage.getItem(storageKey)) continue;
      storage.removeItem(storageKey);
      removed = true;
    }
    const normalized = normalizeWorksheetDraftRowKey(rowKeyOrStorageKey);
    if (normalized) storage.removeItem(`${SAVED_AT_PREFIX}${normalized}`);
    if (removed) notifyWorksheetDraftsChanged();
  } catch {
    /* ignore */
  }
}
